import { Injectable } from '@angular/core';
import createDebug from 'debug';

const debug = createDebug('AramisProcessCenter:PageService');

export interface PageMetadata {
  name: string;
  path: string;
  displayName: string;
  description: string;
  sidenavIcon?: string;
  isDefault: boolean;
  roles: readonly string[];
}

@Injectable({ providedIn: 'root' })
export class PageService {
  readonly names = [
    'EditProcessDesign',
    'Home',
    'Login',
    'MessageListener',
    'MigrateProcessInstances',
    'NotAuthorized',
    'ProcessAdministrators',
    'ProcessConfiguration',
    'ProcessDataType',
    'ProcessDataTypes',
    'ProcessDesign',
    'ProcessDesigns',
    'ProcessFunction',
    'ProcessFunctions',
    'ProcessInstance',
    'ProcessInstances',
    'ProcessMessage',
    'ProcessMessages',
    'ProcessRole',
    'ProcessRoles',
    'ProcessService',
    'ProcessServices',
    'ProcessUserInterface',
    'ProcessUserInterfaces',
    'ServiceTask',
    'ServiceTasks',
    'SwitchUser',
    'Tenant',
    'Tenants',
    'Timer',
    'Token',
    'UserActivity',
    'UserTask',
    'UserTasks',
    'WebhookDeliveries',
    'WebhookDelivery',
    'WebhookSubscription',
    'WebhookSubscriptions',
  ] as const;

  readonly metadata: readonly PageMetadata[] = [
    {
      name: 'EditProcessDesign',
      path: '/edit-process-design/:designUuid',
      displayName: 'Edit Process Design',
      description:
        'Full-screen process modeler for editing a process design. In-progress edits are persisted to localStorage keyed by design UUID so the user can make many changes across sessions before publishing.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Home',
      path: '/home',
      displayName: 'Home',
      description: 'The default landing page.',
      isDefault: true,
      roles: [],
    },
    {
      name: 'Login',
      path: '/login',
      displayName: 'Login',
      description: 'Public login page with a button to initiate OIDC login.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'MessageListener',
      path: '/message-listeners/:messageListenerFormGroup.id',
      displayName: 'Message Listener',
      description:
        'Detail view for a message listener showing its status and the process modeler with activity highlighting.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'MigrateProcessInstances',
      path: '/process-designs/:processDesignFormGroup.id/migrate',
      displayName: 'Migrate Process Instances',
      description:
        'Version-grouped migration view for a target process design version. The left column lists each earlier version with instances to migrate: a ready-to-migrate count with a per-version migrate button, and a row per issue (a stuck activity, Missing or Mapped). Clicking an issue shows the source and target diagrams; clicking a target activity maps the stuck activity to it.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'NotAuthorized',
      path: '/not-authorized',
      displayName: 'Not Authorized',
      description:
        'Displayed when a user authenticates but is not registered in the system.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'ProcessAdministrators',
      path: '/process-administrators',
      displayName: 'Process Administrators',
      description: 'Manage user role assignments.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessConfiguration',
      path: '/process-configuration',
      displayName: 'Configuration',
      description:
        'Landing page with links to configure process design elements.',
      sidenavIcon: 'settings',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessDataType',
      path: '/process-configuration/data-types/:dataType.id',
      displayName: 'Data Type',
      description: 'Detail page for viewing and editing a process data type.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessDataTypes',
      path: '/process-configuration/data-types',
      displayName: 'Data Types',
      description: 'List page for process data types.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessDesign',
      path: '/process-designs/:processDesignFormGroup.id',
      displayName: 'Process Design',
      description:
        'Detail view for a process design with start instance dialog, version history, active instances, and process modeler visualization.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessDesigns',
      path: '/process-designs',
      displayName: 'Process Designs',
      description:
        'Lists all process designs with sorting and refresh capability.',
      sidenavIcon: 'account_tree',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessFunction',
      path: '/process-configuration/functions/:function.id',
      displayName: 'Function',
      description: 'Detail page for viewing and editing a process function.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessFunctions',
      path: '/process-configuration/functions',
      displayName: 'Functions',
      description: 'List page for process functions.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessInstance',
      path: '/process-instances/:processInstanceFormGroup.id',
      displayName: 'Process Instance',
      description:
        'Detail view for a process instance with pause, resume, retry, reprocess, and delete actions, token list, history, and process modeler.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessInstances',
      path: '/process-instances',
      displayName: 'Process Instances',
      description:
        'Lists all process instances with process design, status, and timestamps.',
      sidenavIcon: 'play_circle',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessMessage',
      path: '/process-configuration/messages/:processMessageFormGroup.id',
      displayName: 'Message',
      description: 'Detail page for viewing and editing a process message.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessMessages',
      path: '/process-configuration/messages',
      displayName: 'Messages',
      description: 'List page for process messages.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessRole',
      path: '/process-configuration/roles/:processRoleFormGroup.id',
      displayName: 'Role',
      description: 'Detail page for viewing and editing a process role.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessRoles',
      path: '/process-configuration/roles',
      displayName: 'Roles',
      description: 'List page for process roles.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessService',
      path: '/process-configuration/services/:processServiceFormGroup.id',
      displayName: 'Service',
      description: 'Detail page for viewing and editing a process service.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessServices',
      path: '/process-configuration/services',
      displayName: 'Services',
      description: 'List page for process services.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessUserInterface',
      path: '/process-configuration/user-interfaces/:userInterface.id',
      displayName: 'User Interface',
      description:
        'Detail page for viewing and editing a process user interface.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ProcessUserInterfaces',
      path: '/process-configuration/user-interfaces',
      displayName: 'User Interfaces',
      description: 'List page for process user interfaces.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ServiceTask',
      path: '/service-tasks/:serviceTaskFormGroup.id',
      displayName: 'Service Task',
      description:
        'Detail view for a service task with claim, complete, and report error actions.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'ServiceTasks',
      path: '/service-tasks',
      displayName: 'Service Tasks',
      description:
        'Lists all service tasks with process design, queue, status, and assignment information.',
      sidenavIcon: 'settings',
      isDefault: false,
      roles: [],
    },
    {
      name: 'SwitchUser',
      path: '/switch-user',
      displayName: 'Switch User',
      description:
        'Allows administrators to switch to another user by selecting from a list. Only available when ALLOW_IMPERSONATION is enabled on the server. Selecting a user sets sessionStorage and reloads the app.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Tenant',
      path: '/tenants/:tenant.id',
      displayName: 'Tenant',
      description:
        'Administration page for a single tenant and its user assignments.',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'Tenants',
      path: '/tenants',
      displayName: 'Tenants',
      description: 'Administration page listing all tenants.',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'Timer',
      path: '/timers/:timerFormGroup.id',
      displayName: 'Timer',
      description:
        'Detail view for a timer showing its status, fire time, and the process modeler with activity highlighting.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'Token',
      path: '/tokens/:tokenFormGroup.id',
      displayName: 'Token',
      description:
        'Detail view for a token showing related user tasks, service tasks, history, and process modeler with activity highlighting.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'UserActivity',
      path: '/user-activity',
      displayName: 'User Activity',
      description:
        'Administrator report of active time by user for a selected range. The range is chosen as a period unit (hour, day, week, month) plus a relative choice (e.g. "This Week" or "Last Week"). Active time is the number of activity periods times the period length.',
      sidenavIcon: 'insights',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'UserTask',
      path: '/user-tasks/:userTaskFormGroup.id',
      displayName: 'User Task',
      description:
        'Detail view for a user task with claim, unclaim, reassign, update data, and complete actions.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'UserTasks',
      path: '/user-tasks',
      displayName: 'User Tasks',
      description:
        'Lists all user tasks with process design, assignment, and status information.',
      sidenavIcon: 'assignment_ind',
      isDefault: false,
      roles: [],
    },
    {
      name: 'WebhookDeliveries',
      path: '/webhook-deliveries',
      displayName: 'Webhook Deliveries',
      description:
        'Lists the newest deliveries across every subscription, filterable by status and by subscription. Rows open the delivery page, which shows the request and response and offers redelivery.',
      sidenavIcon: 'send',
      isDefault: false,
      roles: ['WebhookAdministrator'],
    },
    {
      name: 'WebhookDelivery',
      path: '/webhook-deliveries/:webhookDelivery.id',
      displayName: 'Webhook Delivery',
      description:
        'Shows one delivery in full: the envelope that was (or will be) sent, the most recent response and error, and the claim and retry state. Failed and Dead deliveries can be queued again from here.',
      isDefault: false,
      roles: ['WebhookAdministrator'],
    },
    {
      name: 'WebhookSubscription',
      path: '/webhook-subscriptions/:webhookSubscription.id',
      displayName: 'Webhook Subscription',
      description:
        'Edits one subscription: receiver URL, event types, filter expression, static headers, and enabled flag, with autosave. Offers secret rotation (shown once), a test event, one-click re-enable after an auto-disable, and the most recent deliveries with redelivery.',
      isDefault: false,
      roles: ['WebhookAdministrator'],
    },
    {
      name: 'WebhookSubscriptions',
      path: '/webhook-subscriptions',
      displayName: 'Webhook Subscriptions',
      description:
        'Lists every subscription with its enabled state, last delivery, and failure streak, and opens the create dialog.',
      sidenavIcon: 'webhook',
      isDefault: false,
      roles: ['WebhookAdministrator'],
    },
  ];

  readonly sidenavPages: readonly PageMetadata[] = [
    this.metadata.find((p) => p.name === 'ProcessConfiguration')!,
    this.metadata.find((p) => p.name === 'ProcessDataTypes')!,
    this.metadata.find((p) => p.name === 'ProcessFunctions')!,
    this.metadata.find((p) => p.name === 'Home')!,
    this.metadata.find((p) => p.name === 'ProcessMessages')!,
    this.metadata.find((p) => p.name === 'ProcessAdministrators')!,
    this.metadata.find((p) => p.name === 'ProcessDesigns')!,
    this.metadata.find((p) => p.name === 'ProcessInstances')!,
    this.metadata.find((p) => p.name === 'ProcessRoles')!,
    this.metadata.find((p) => p.name === 'ServiceTasks')!,
    this.metadata.find((p) => p.name === 'ProcessServices')!,
    this.metadata.find((p) => p.name === 'Tenants')!,
    this.metadata.find((p) => p.name === 'UserActivity')!,
    this.metadata.find((p) => p.name === 'ProcessUserInterfaces')!,
    this.metadata.find((p) => p.name === 'UserTasks')!,
    this.metadata.find((p) => p.name === 'WebhookDeliveries')!,
    this.metadata.find((p) => p.name === 'WebhookSubscriptions')!,
  ];

  getMetadata(name: string): PageMetadata | undefined {
    return this.metadata.find((m) => m.name === name);
  }
}
